<template>
    <div fluid class="permissions-tab-view">
        <div >
            <div v-if="latestModelisation && initialized">
                <v-row no-gutters>
                    <v-col cols="2">
                        <div style="display: flex;">
                            <v-container>
                                <timeline
                                    class="fullscreen-timeline scrollbar-custom"
                                    :items="items"
                                    :selectedItem="selectedModelisation"
                                    @selected-item="switchModelisation"
                                ></timeline>
                            </v-container>
                            <v-divider :vertical="true"></v-divider>
                        </div>
                    </v-col>
                    <v-col cols="10"  class="right-part scrollbar-custom">
                        <v-container v-if="modelsMinimumLevel.length">
                            <div v-html="$t('instance.permissions.concrete_link_text', {concretePermissionsDoc: permissionLink})">
                            </div>
                            <v-row dense>
                                <v-col cols="12" align-self="center">
                                    <v-alert
                                        :value="isReadOnly"
                                        type="info"
                                    >
                                        {{$t('instance.permissions.related_previous_datamodel_text')}}
                                    </v-alert>
                                </v-col>
                                <v-col cols="12" align-self="center" class="is-scoped-checkbox">
                                    <v-checkbox
                                        v-model="isScoped"
                                        :label="$t('instance.permissions.enable_scope')"
                                        :disabled="isReadOnly"
                                    >
                                        <div slot="append">
                                            <v-tooltip top>
                                                <template v-slot:activator="{ on }">
                                                    <v-icon small dark v-on="on">info</v-icon>
                                                </template>
                                                <span style="font-size:14px;">{{$t('instance.permissions.able_group_objects_text')}}</span>
                                            </v-tooltip>
                                        </div>
                                    </v-checkbox>
                                </v-col>
                                <v-col  v-if="isScoped" cols="4" align-self="center"> {{$t('instance.permissions.scope_defined_by_model')}}</v-col>
                                <v-col  v-if="isScoped" cols="8" align-self="center">
                                    <v-select
                                        v-model="scopeModelUid"
                                        :items="modelsMinimumLevel"
                                        item-text="name"
                                        item-value="uid"
                                        :disabled="isReadOnly"
                                        @change="changeScopeModel(scopeModelUid)"
                                    ></v-select>
                                </v-col>
                            </v-row>
                        </v-container>
                        <v-container>
                            {{$t('instance.permissions.define_action_all_models_text')}}
                        </v-container>

                        <v-container>
                            <v-expansion-panels>
                                <v-expansion-panel>
                                    <v-expansion-panel-header>
                                        <div>
                                            {{$t('instance.permissions.apply_all_models_text')}}
                                            <small style="font-size: 12px">
                                                {{$t('instance.permissions.apply_all_models_empty_text')}}
                                            </small>
                                        </div>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <v-container>
                                            <div class="select-for-all-models">
                                                <v-select
                                                    v-model="permissionsToApplyToAllModels.create"
                                                    :items="levelForApplyToAll"
                                                    :label="$t('instance.permissions.name_create')"
                                                    :hint="levelHint(permissionsToApplyToAllModels.create, $t('instance.permissions.apply_all_array_text_create'))"
                                                    :disabled="isReadOnly"
                                                    persistent-hint
                                                ></v-select>
                                            </div>
                                            <div class="select-for-all-models">
                                                <v-select
                                                    v-model="permissionsToApplyToAllModels.retrieve"
                                                    :items="levelForApplyToAll"
                                                    :label="$t('instance.permissions.name_retrieve')"
                                                    :hint="levelHint(permissionsToApplyToAllModels.retrieve, $t('instance.permissions.apply_all_array_text_retrieve'))"
                                                    :disabled="isReadOnly"
                                                    persistent-hint
                                                ></v-select>
                                            </div>
                                            <div class="select-for-all-models">
                                                <v-select
                                                    v-model="permissionsToApplyToAllModels.update"
                                                    :items="levelForApplyToAll"
                                                    :label="$t('instance.permissions.name_update')"
                                                    :hint="levelHint(permissionsToApplyToAllModels.update, $t('instance.permissions.apply_all_array_text_update'))"
                                                    :disabled="isReadOnly"
                                                    persistent-hint
                                                ></v-select>
                                            </div>
                                            <div class="select-for-all-models">
                                                <v-select
                                                    v-model="permissionsToApplyToAllModels.delete"
                                                    :items="levelForApplyToAll"
                                                    :label="$t('instance.permissions.name_delete')"
                                                    :hint="levelHint(permissionsToApplyToAllModels.delete, $t('instance.permissions.apply_all_array_text_delete'))"
                                                    :disabled="isReadOnly"
                                                    persistent-hint
                                                ></v-select>
                                            </div>
                                            <v-btn
                                                color="info"
                                                class="apply-for-all-models"
                                                :disabled="applyForAllModelsBtnDisabled"
                                                @click="applyForAllModels"
                                            >{{$t('instance.permissions.apply_all_btn')}}
                                            </v-btn>
                                        </v-container>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-container>

                        <v-container>
                            <v-text-field
                                v-model="search"
                                append-icon="search"
                                :label="$t('instance.permissions.search_model')"
                                single-line
                                hide-details
                            ></v-text-field>
                        </v-container>
                        <v-container>
                            <v-dialog
                                v-model="dialog"
                                max-width="500px"
                                :persistent="true"
                                :no-click-animation="true"
                            >
                                <v-card dark>
                                    <v-card-title>
                                        <span class="permission-headline">{{$t('instance.permissions.edit')}} {{editedItem.name}}</span>
                                    </v-card-title>

                                    <v-card-text>
                                        <v-container>
                                            <v-row dense>
                                                <v-col v-for="action in actions" :key="action.key" cols="12">
                                                    <v-select
                                                        v-model="editedItem[action.key]"
                                                        :items="levels"
                                                        :label="action.name"
                                                        :hint="levelHint(editedItem[action.key], action.name)"
                                                        :disabled="isReadOnly"
                                                        persistent-hint
                                                    ></v-select>
                                                </v-col>
                                                <template v-if="isScoped && editedItem.uid !== scopeModelUid">
                                                    <v-col cols="12">
                                                        <v-checkbox
                                                            v-model="editedItem.is_scoped"
                                                            :disabled="isReadOnly"
                                                        >
                                                            <template slot="label">
                                                                {{$t('instance.permissions.scope_model_by_relatedmodel', { model_name: editedItem.name
                                                                }, {
                                                                    related_uid :relatedModelName(scopeModelUid)
                                                                })}} ?
                                                            </template>
                                                        </v-checkbox>
                                                    </v-col>
                                                </template>
                                                <template v-else-if="isScoped">
                                                    <br>
                                                    {{$t('instance.permissions.this_is_scope_model_text')}}
                                                </template>
                                            </v-row>
                                        </v-container>
                                    </v-card-text>
                                    <v-divider></v-divider>
                                    <v-card-actions>
                                        <v-spacer></v-spacer>
                                        <div class="dialog-confirm-buttons">
                                            <v-btn @click="closeDialog">{{$t('instance.permissions.cancel_btn')}}</v-btn>
                                            <v-btn color="info" @click="saveDialog">{{$t('instance.permissions.save_btn')}}</v-btn>
                                        </div>
                                    </v-card-actions>
                                </v-card>
                            </v-dialog>
                            <v-data-table
                                v-if="modelsMinimumLevel.length"
                                :headers="computedHeaders"
                                :hide-default-header="true"
                                :items="modelsMinimumLevel"
                                :options.sync="paginationOptions"
                                :footer-props="{
                                    showFirstLastPage: true,
                                    itemsPerPageText:$t('instance.permissions.pagination_text'),
                                    itemsPerPageOptions:paginationOptions.itemsRange,
                                }"
                                :search="search"
                            >
                                <template v-slot:header="{ props: { headers } }">
                                    <thead>
                                        <tr>
                                            <th
                                                v-for="header in headers"
                                            >
                                                <v-icon small>{{header.icon }}</v-icon> {{header.text }}
                                            </th>
                                        </tr>
                                    </thead>
                                </template>
                                <template slot="item" slot-scope="props">
                                    <tr>
                                        <td v-for="header in computedHeaders" :key="header.value">
                                            <v-btn
                                                v-if="header.value === 'edit' "
                                                text
                                                icon
                                                :disabled="isReadOnly"
                                                @click="editItem(props.item)"
                                            >
                                                <v-icon
                                                    small
                                                    class="edit-icon"
                                                >
                                                    edit
                                                </v-icon>
                                            </v-btn>
                                            <span v-else-if="header.value === 'is_scoped'">
                                                <template v-if="scopeModelUid === props.item.uid">
                                                    <v-icon small disabled>cancel</v-icon>
                                                    <span style="color: hsla(0,0%,100%,.5);">{{$t('instance.permissions.is_not_scoped')}}</span>
                                                </template>
                                                <template v-else>
                                                    <v-icon small>
                                                        {{ props.item.is_scoped ? 'check_circle' : 'cancel' }}
                                                    </v-icon> {{ props.item.is_scoped ? $t('instance.permissions.is_scoped') : $t('instance.permissions.is_not_scoped') }}
                                                </template>
                                            </span>
                                            <span v-else>
                                                {{ props.item[header.value] }}
                                            </span>
                                        </td>
                                    </tr>
                                </template>
                                <!-- Lint error is false error -->
                                <template v-slot:[`footer.page-text`]="items">
                                  {{ items.pageStart }} - {{ items.pageStop }} {{ $t('instance.permissions.pagination_and') }} {{ items.itemsLength }}
                                </template>
                            </v-data-table>
                        </v-container>
                    </v-col>
                </v-row>
            </div>
            <div v-else>
                <v-alert
                    type="error"
                >
                    No datamodel available.
                </v-alert>
            </div>
        </div>
        <dialog-confirm
            ref="confirm"
        ></dialog-confirm>
    </div>
</template>

<script>

import Timeline from '@/components/Partials/timeline.vue';
import moment from 'moment';
import DialogConfirm from '@/components/Partials/dialog-confirm.vue';
import { cloneDeep } from 'lodash';

export default {

    components: {
        Timeline,
        DialogConfirm,
    },

    props: {
        modelisations: {
            type: Array,
            required: true,
        },
        schema: {
            type: Object,
            required: true,
        },
        selectedModelisation:{
            type: Object,
        },
        latestModelisation:{
            type: Object,
        }
    },

    data() {
        return {
            // scope options
            isScoped: false,
            scopeModelUid: '',
            scopeModelName: '',

            // edition dialog
            dialog: false,
            editedItem: {},

            // table settings
            search: '',
            actions: [
                {
                    name: `${this.$t('instance.permissions.name_create')}`,
                    key: 'create',
                },
                {
                    name: `${this.$t('instance.permissions.name_retrieve')}`,
                    key: 'retrieve',
                },
                {
                    name: `${this.$t('instance.permissions.name_update')}`,
                    key: 'update',
                },
                {
                    name: `${this.$t('instance.permissions.name_delete')}`,
                    key: 'delete',
                },
            ],
            levels: ['superuser', 'admin', 'manager', 'authenticated', 'anonymous'],
            initialized: false,
            permissionsToApplyToAllModels: {
                retrieve: '',
                create: '',
                update: '',
                delete: '',
            },
            paginationOptions:{
                page: 1,
                itemsPerPage: 10,
                itemsRange:[
                    5,
                    10,
                    25,
                    {
                        'text':this.$t('instance.query_fields.index.pagination_all'),
                        'value':-1
                    }
                ],
            },
        };
    },

    computed: {
        permissionLink(){
            return window.config.concrete_permissions_documentation;
        },
        applyForAllModelsBtnDisabled (){
            return Object.values(this.permissionsToApplyToAllModels).every((val) => val === "");
        },
        levelForApplyToAll (){
            let levels = this.levels.slice();
            levels.unshift('');
            return levels;
        },
        items (){
            let cloneModelisation = cloneDeep(this.modelisations);
            return cloneModelisation.sort((a,b) => new Date(b.creation_date)-new Date(a.creation_date))
                .map((model) => {
                    return {
                        title: `v${model.version}`,
                        text: moment(model.creation_date).format('YYYY/MM/DD'),
                        uid: model.uid,
                        readOnly: !model.editable,
                    };
                });
        },
        computedHeaders() {
            if(this.isScoped) {
                return [
                    { text: `${this.$t('instance.permissions.computedHeaders_text_model')}`, align: 'left', value: 'name'},
                    { text: `${this.$t('instance.permissions.name_create')}`, align: 'left', value: 'create', sortable: false, icon: 'add'},
                    { text: `${this.$t('instance.permissions.name_retrieve')}`, align: 'left', value: 'retrieve', sortable: false, icon: 'save_alt'},
                    { text: `${this.$t('instance.permissions.name_update')}`, align: 'left', value: 'update', sortable: false, icon: 'create'},
                    { text: `${this.$t('instance.permissions.name_delete')}`, align: 'left', value: 'delete', sortable: false, icon: 'delete'},
                    { text: `${this.$t('instance.permissions.computedHeaders_is_scoped')}`, align: 'left', value: 'is_scoped', sortable: false,},
                    { text: `${this.$t('instance.permissions.computedHeaders_edit_item')}`, align: 'left', value: 'edit', sortable: false }
                ];
            }
            return [
                { text: `${this.$t('instance.permissions.computedHeaders_text_model')}`, align: 'left', value: 'name'},
                { text: `${this.$t('instance.permissions.name_create')}`, align: 'left', value: 'create', sortable: false, icon: 'add'},
                { text: `${this.$t('instance.permissions.name_retrieve')}`, align: 'left', value: 'retrieve', sortable: false, icon: 'save_alt'},
                { text: `${this.$t('instance.permissions.name_update')}`, align: 'left', value: 'update', sortable: false, icon: 'create'},
                { text: `${this.$t('instance.permissions.name_delete')}`, align: 'left', value: 'delete', sortable: false, icon: 'delete'},
                { text: `${this.$t('instance.permissions.computedHeaders_edit_item')}`, align: 'left', value: 'edit', sortable: false }
            ];

        },
        modelsMinimumLevel() {
            // Used to display the data table
            if(this.schema && this.schema.modelList) {
                return this.schema.modelList.map((model) => {
                    // Init values of table with data from the latest datamodel
                    let permissionsOfModel = this.schema.permissionList.find((permission)=> {
                        return permission.model_uid === model.uid;
                    });
                    return {
                        name: model.name,
                        uid: model.uid,
                        create: permissionsOfModel.createLevel,
                        retrieve: permissionsOfModel.retrieveLevel,
                        update: permissionsOfModel.updateLevel,
                        delete: permissionsOfModel.deleteLevel,
                        is_scoped: permissionsOfModel.lookups.length === 0 ? false : true,
                    };
                }).filter(Boolean).sort((a, b) => (a.name > b.name) ? 1 : -1);
            }
            return [];
        },

        scopeValue() {
            return this.editedItem.is_scoped ? [
                {
                    model_name: this.scopeModelName,
                    model_uid: this.scopeModelUid,
                }] : [];
        },


        isReadOnly() {
            return this.schema.version !== this.latestModelisation.version;
        },
    },

    watch: {
        schema: {
            handler (val){
                if(!this.initialized){
                    this.init();
                }
            },
            deep: true,
        },
        isScoped (val, oldVal){
            if(oldVal === true & val === false){
                this.$refs.confirm.open(
                    `${this.$t('instance.permissions.isScoped_text')}`,
                    `${this.$t('instance.permissions.isScoped_text_confirmation')}`
                ).then((response) => {
                    if(!response){
                        this.isScoped = true;
                    }else{
                        this.patchScope();
                    }
                });
            }else if (oldVal === false & val === true){
                this.patchScope();
            }
        }
    },

    mounted: function(){
        this.$nextTick(()=>{
            this.init()
        })
    },

    methods: {
        init(){
            if(this.latestModelisation) {
                // Init default value of scope model
                if(this.schema.isScoped) {
                    this.scopeModelName = this.schema.scopeModel.name;
                    this.scopeModelUid = this.schema.scopeModel.uid;
                    this.isScoped = true;
                } else {
                    this.scopeModelUid = this.modelsMinimumLevel[0].uid;
                    this.scopeModelName = this.modelsMinimumLevel[0].name;
                }
            }
            this.initialized = true;
        },
        applyForAllModels (){
            for(let perm of this.schema.permissionList){
                if(perm){
                    perm.setMinimumLevels(
                        {
                            create: this.permissionsToApplyToAllModels.create !== '' ? this.permissionsToApplyToAllModels.create : perm.minimum_levels.create,
                            retrieve: this.permissionsToApplyToAllModels.retrieve !== '' ? this.permissionsToApplyToAllModels.retrieve : perm.minimum_levels.retrieve,
                            update: this.permissionsToApplyToAllModels.update !== '' ? this.permissionsToApplyToAllModels.update : perm.minimum_levels.update,
                            delete: this.permissionsToApplyToAllModels.delete !== '' ? this.permissionsToApplyToAllModels.delete : perm.minimum_levels.delete,
                        }
                    );
                }
            }
            this.patchPermissions();
        },
        relatedModelName(uid) {
            let model = this.modelsMinimumLevel.find((model) => {
                return uid === model.uid;
            });
            return model ? model.name : '';
        },

        levelHint(level, action) {
            if(level === 'authenticated') {
                return `${this.$t('instance.permissions.levelHint_auth', {action : action})}`;
            } else if(level === 'manager') {
                return `${this.$t('instance.permissions.levelHint_manager', {action : action})}`;
            } else if(level === 'admin') {
                return `${this.$t('instance.permissions.levelHint_admin', {action : action})}`;
            } else if(level === 'superuser') {
                return `${this.$t('instance.permissions.levelHint_superuser', {action : action})}`;
            }
            return '';
        },

        coherentLevel(level) {
            if (level === 'manager' || level === 'staff') {
                return 'manager';
            } else if (level === 'authenticated' || level === 'simpleuser'){
                return 'authenticated';
            } else {
                return level;
            }
        },

        editItem (item) {
            this.editedItem = Object.assign({}, item);
            this.dialog = true;
        },

        changeScopeModel(scopeModelUid, setSnackbar=true) {
            this.scopeModelName = this.modelsMinimumLevel.find((model) => {
                return scopeModelUid === model.uid;
            }).name;
            this.patchScope(setSnackbar);
        },

        updatePermissions() {

            let updatedPermission = this.schema.permissionList.find((permission) => {
                return permission.model_uid === this.editedItem.uid;
            });

            updatedPermission.setMinimumLevels(
                {
                    create: this.editedItem.create,
                    retrieve: this.editedItem.retrieve,
                    update: this.editedItem.update,
                    delete: this.editedItem.delete,
                }
            );
            updatedPermission.setLookups(this.scopeValue);
        },

        updateScope(isScoped) {

            if(isScoped) {
                for (let permission of this.schema.permissionList) {
                    if(permission.lookups.length !== 0) {
                        if(permission.model_uid === this.scopeModelUid) {
                            permission.setLookups([]);
                        } else {
                            permission.setLookups([{
                                model_name: this.scopeModelName,
                                model_uid: this.scopeModelUid,
                            }]);
                        }
                    }
                }
            } else {
                for (let permission of this.schema.permissionList) {
                    permission.setLookups([]);
                }
            }
        },

        patchScope(setSnackbar=true) {
            this.updateScope(this.isScoped);
            this.$store.commit('SET_SAVE_STATE', 'sync');
            this.$store.dispatch('patchItem', {
                item: {
                    url: this.latestModelisation.url,
                    value: this.schema.schema,
                },
                model: 'modelisations'
            }).then(() => {
                if(setSnackbar) {
                    this.$store.commit('SET_SNACKBAR', {
                        color: 'success',
                        icon: 'done',
                        text:   `${this.$t('instance.permissions.patchScope_success_text',
                                    {latestModelisation_label : this.latestModelisation.label}
                                )}`,
                    });
                    this.$store.commit('SET_DISPLAY_SNACKBAR', true);
                }
                this.$store.commit('SET_SAVE_STATE', 'upToDate');
                this.$eventBus.$emit('update-data-model', this.schema.schema);
            }).catch(() => {
                if(setSnackbar) {
                    this.$store.commit('SET_SNACKBAR', {
                        color: 'error',
                        icon: 'error',
                        text: `${this.$t('instance.permissions.patchScope_error_text',
                                    {latestModelisation_label : this.latestModelisation.label}
                                )}`,
                    });
                    this.$store.commit('SET_SAVE_STATE', 'error');
                    this.$store.commit('SET_DISPLAY_SNACKBAR', true);
                }
            });
        },

        patchPermissions(setSnackbar=true) {
            this.$store.commit('SET_SAVE_STATE', 'sync');
            this.$store.dispatch('patchItem', {
                item: {
                    url: this.latestModelisation.url,
                    value: this.schema.schema,
                },
                model: 'modelisations'
            }).then(() => {
                if(setSnackbar) {
                    this.$store.commit('SET_SNACKBAR', {
                        color: 'success',
                        icon: 'done',
                        text: `${this.$t('instance.permissions.patchPermissions_succes_text',
                                    {latestModelisation_label : this.latestModelisation.label}
                                )}`,
                    });
                    this.$store.commit('SET_DISPLAY_SNACKBAR', true);
                }
                this.$store.commit('SET_SAVE_STATE', 'upToDate');
                this.$eventBus.$emit('update-data-model', this.schema.schema);
            }).catch(() => {
                if(setSnackbar) {
                    this.$store.commit('SET_SNACKBAR', {
                        color: 'error',
                        icon: 'error',
                        text: `${this.$t('instance.permissions.patchPermissions_error_text',
                                    {latestModelisation_label : this.latestModelisation.label}
                                )}`,
                    });
                    this.$store.commit('SET_SAVE_STATE', 'error');
                    this.$store.commit('SET_DISPLAY_SNACKBAR', true);
                }
            });
        },

        closeDialog() {
            this.dialog = false;
        },


        saveDialog() {
            this.updatePermissions();
            this.patchPermissions();
            this.closeDialog();
        },
        switchModelisation (modelisation){
            let relatedModelisation = this.modelisations.find((_modelisation) => _modelisation.uid === modelisation.uid);
            this.$emit('update-schema-modelisation', relatedModelisation.value, relatedModelisation.version);
        },
    },
};
</script>

<style lang="scss">
$top-bar-height: 64px;
$tabs-height: 48px;
$overflow-height: $top-bar-height + $tabs-height;
.permissions-tab-view {
    height: calc( 100vh - #{$overflow-height} );

    .left-part{
        height: 100%;
    }
    .right-part{
        // 64px topbar
        // 48px tabs
        height: calc( 100vh - #{$overflow-height} );
        overflow: auto;
        padding: 1rem;
        .apply-for-all-models{
            display: flex;
            margin-left: auto;
            font-size: 14px;
        }
        .select-for-all-models {
            margin-top: 15px;
        }
    }

    .is-scoped-checkbox {
        .v-input--selection-controls__input {
            margin-bottom: 5px;
        }
    }

    .edit-icon {
        padding: 10px;
    }

    .form-scope-text {
        font-size: 13px !important;
        color: rgba(0, 0, 0, 0.65) !important;
        font-weight: 600 !important;
    }
}
.permission-headline{
    color: $main-light-color;
}
</style>



// WEBPACK FOOTER //
// src/components/page/Instance/Permissions/index.vue